<script setup lang="ts">
import { computed } from 'vue';
import { useNowTicker } from '@/composables/useNowTicker';
import { useModelRecordQuery } from '@/composables/api/useModelRecordQuery';
import { liveRecordPollMsFor } from '@/composables/searchInspectorRegistry';
import { checkinStatusColor, formatCompactDuration, secondsSince } from '@/utils/checkinStatus';
import { useZoneIcon } from '@/composables/useZoneIcon';
import PlayerTypeIcon from '@/components/DataTable/PlayerTypeIcon.vue';
import ZoneDisplayString from '@/components/ZoneDisplayString.vue';
import { readZoneDisplayString, splitZoneDisplayString } from '@/utils/zoneDisplayString';
import { useRecordHeading } from '@/composables/recordLayout/useRecordHeading';

/**
 * Header for the inspector overlay, laid out like the player card so the same
 * player reads the same way in both places.
 *
 * The card leads with the ZONE name and puts the location underneath; this used
 * to lead with the LOCATION and trail the zone as muted small text, so "4
 * Monahan PLC / Kunze, Pfannerstill and Schulist" on the card became "4 Kunze,
 * Pfannerstill and Schulist  Monahan PLC" in the inspector. Same record, two
 * different-looking headers.
 *
 * The large `title` variant draws the same two rows as the player's own page,
 * from the person's layout: the top line, such as "Player 6 · FrankPostTEST ·
 * Sauce Industries", over the title. What only the peek has sits beside them:
 * the player-type icon and the software version beside the top line, and the
 * live check-in dot, the zone icon and the time since the last check-in beside
 * the title. A layout with no top line drops that row, and the icon and the
 * version with it. The slim `bar` variant is a one-line strip with no room for
 * a second row, so it keeps everything on one line — but in the card's order,
 * zone first.
 *
 * Used in place of the "LocationZonePlayer #6" title text. Receives either the
 * row directly (master-row overlay path) or entityId + recordId (id-column
 * side-peek path). Either way it subscribes to the polling record query the
 * inspector content uses (shared key → one request), so the status light and
 * timer track live check-ins instead of freezing on the row it was opened with.
 */
const props = defineProps<{
    row?: Record<string, unknown>;
    entityId?: string;
    recordId?: number | string;
    /** `bar` = slim title-bar line; `title` = the large heading above the section switcher. */
    size?: 'bar' | 'title';
}>();

const ENTITY_ID = 'LocationZonePlayer';


const recordId = computed<number | string | null>(() => {
    if (props.recordId != null) {
        return props.recordId;
    }
    const id = props.row?.id;
    return typeof id === 'number' || typeof id === 'string' ? id : null;
});

const recordQuery = useModelRecordQuery(ENTITY_ID, recordId, {
    refetchIntervalMs: liveRecordPollMsFor(ENTITY_ID),
});

// Prefer the live record; fall back to the row prop until the first fetch lands.
const effectiveRow = computed<Record<string, unknown> | null>(
    () => recordQuery.data.value ?? props.row ?? null
);

const nowMs = useNowTicker();

const lastCheckinAt = computed<string | null>(() => {
    const flattened = effectiveRow.value?.player_data_updated_at;
    return typeof flattened === 'string' && flattened !== '' ? flattened : null;
});

const checkinSeconds = computed(() => secondsSince(lastCheckinAt.value, nowMs.value));
const statusColor = computed(() => checkinStatusColor(checkinSeconds.value));

const statusDotClass = computed(
    () =>
        ({
            green: 'bg-green-500',
            red: 'bg-red-500',
        })[statusColor.value]
);

function readDisplayString(value: unknown): string | null {
    if (value && typeof value === 'object' && 'display_string' in value) {
        const display = (value as { display_string?: unknown }).display_string;
        return typeof display === 'string' && display.trim() !== '' ? display : null;
    }
    return null;
}

const displayId = computed(() => effectiveRow.value?.id ?? props.recordId ?? null);
const locationName = computed(() => readDisplayString(effectiveRow.value?.location));

/**
 * Whether the one-line strip still needs to name the location after the zone.
 *
 * The zone name above is the full display string with each half separately linked, so when
 * it splits it already names the location and trailing it again put the client on the strip
 * twice. When it does not split — a zone with no location, or one indexed before the prefix
 * existed — the strip names it again, because there it is the only clue to which client
 * this is.
 *
 * Width is the other reason: the account is kept off this variant precisely so a second
 * name cannot truncate the zone, and a duplicate location did the damage the account was
 * barred for.
 */
const showTrailingLocation = computed<boolean>(() => {
    if (!locationName.value) {
        return false;
    }

    return splitZoneDisplayString(
        readZoneDisplayString(effectiveRow.value?.location_zone),
        readZoneDisplayString(effectiveRow.value?.location)
    ).location === null;
});

// Same zone mark the search cards show, so the inspector header reads the same.
const zoneIcon = useZoneIcon(() => effectiveRow.value?.location_zone);

const isTitle = computed(() => props.size === 'title');

/** The top line and the title from the person's layout, as the player's page draws them. */
const heading = useRecordHeading(ENTITY_ID, recordId, effectiveRow);

// Player type nests as {id, display_string} in the ES row; the icon needs the id.
const playerTypeId = computed<number | null>(() => {
    const playerType = effectiveRow.value?.player_type;
    if (playerType && typeof playerType === 'object' && 'id' in playerType) {
        const id = (playerType as { id?: unknown }).id;
        return typeof id === 'number' ? id : null;
    }
    return null;
});

const playerBrand = computed<string | null>(() => {
    const brand = effectiveRow.value?.brand;
    return typeof brand === 'string' ? brand : null;
});

// Device-reported software version, normalized so it renders as one "v".
const playerVersion = computed<string | null>(() => {
    const version = effectiveRow.value?.player_data_version;
    if (typeof version !== 'string') {
        return null;
    }
    const trimmed = version.trim().replace(/^v/i, '');
    return trimmed === '' ? null : trimmed;
});
</script>

<template>
    <!-- The title variant: the layout's top line over its title, each with the
         peek's own extras at its ends, on the card's rail grid. -->
    <div
        v-if="isTitle"
        class="grid min-w-0 grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-x-1.5 gap-y-0.5"
        data-testid="lzp-overlay-header"
    >
        <template v-if="heading.topLine">
            <div class="flex justify-center">
                <PlayerTypeIcon :player-type-id="playerTypeId" :brand="playerBrand" />
            </div>

            <p
                class="text-muted-foreground min-w-0 truncate text-sm leading-snug"
                :title="heading.topLine"
                data-testid="record-peek-top-line"
            >
                {{ heading.topLine }}
            </p>

            <!-- The first line keeps clear of the buttons a host puts in its top-right
                 corner. The record pane sets `--record-header-actions-width` to their
                 width. Everywhere else it is unset and the margin is zero. -->
            <p
                v-if="playerVersion"
                class="text-muted-foreground text-right font-mono text-xs leading-snug tabular-nums mr-[var(--record-header-actions-width,0px)]"
                data-testid="lzp-overlay-header-version"
                :title="`Software version ${playerVersion}`"
            >
                v{{ playerVersion }}
            </p>
            <span v-else class="mr-[var(--record-header-actions-width,0px)]" />
        </template>

        <div class="flex justify-center">
            <span
                class="inline-block size-2.5 shrink-0 rounded-full"
                :class="statusDotClass"
                role="img"
                :aria-label="`Check-in status: ${statusColor}`"
                :data-status="statusColor"
            />
        </div>

        <div class="flex min-w-0 items-center gap-2">
            <component
                :is="zoneIcon"
                v-if="zoneIcon"
                class="size-3.5 shrink-0"
                aria-hidden="true"
                data-testid="lzp-overlay-zone-icon"
            />
            <h2
                class="min-w-0 truncate text-lg leading-tight font-semibold"
                :title="heading.title"
                data-testid="record-peek-title"
            >
                {{ heading.title }}
            </h2>
        </div>

        <!-- With no top line, this is the first line, so it keeps clear of the buttons. -->
        <span
            v-if="checkinSeconds !== null"
            class="text-muted-foreground justify-self-end font-mono text-xs tabular-nums"
            :class="heading.topLine ? '' : 'mr-[var(--record-header-actions-width,0px)]'"
            data-testid="lzp-overlay-header-elapsed"
        >
            {{ formatCompactDuration(checkinSeconds) }}
        </span>
        <span
            v-else
            class="text-muted-foreground justify-self-end text-xs"
            :class="heading.topLine ? '' : 'mr-[var(--record-header-actions-width,0px)]'"
            data-testid="lzp-overlay-header-never"
        >never</span>
    </div>

    <!-- Same grid as the card header: rail glyph, text, right-aligned meta. -->
    <div
        v-else
        class="grid min-w-0 grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-x-1.5 gap-y-0.5"
        data-testid="lzp-overlay-header"
    >
        <div class="flex justify-center">
            <span
                class="inline-block size-2.5 shrink-0 rounded-full"
                :class="statusDotClass"
                role="img"
                :aria-label="`Check-in status: ${statusColor}`"
                :data-status="statusColor"
            />
        </div>

        <div class="flex min-w-0 items-baseline gap-2">
            <span
                v-if="displayId != null"
                class="shrink-0 font-mono text-sm font-semibold tabular-nums"
            >
                {{ displayId }}
            </span>
            <component
                :is="zoneIcon"
                v-if="zoneIcon"
                class="size-3.5 shrink-0 self-center"
                aria-hidden="true"
                data-testid="lzp-overlay-zone-icon"
            />
            <span class="min-w-0 truncate text-sm leading-tight font-semibold">
                <ZoneDisplayString :zone="effectiveRow?.location_zone" :location="effectiveRow?.location" />
            </span>
            <!-- The location trails the zone only when the zone name does not already
                 begin with it. A zone's display string is "{location}-{zone}", so
                 normally it does, and repeating it read "FrankPostTEST-Patio
                 FrankPostTEST" — width spent on a word already on screen. Width is
                 the point here: the account is kept off this one-line variant
                 precisely so a second name cannot truncate the zone, and a duplicate
                 location was doing the damage the account was barred for.

                 Dropping it unconditionally would be wrong, though, because a bare
                 zone name still reaches this component from two live paths: the
                 window between deploying the display-string fix and finishing the
                 reindex, and any record not in the index, which falls back to
                 GET /api/{slug}/{id}. There the location would go from shown twice
                 to not shown at all — strictly worse than the duplication. -->
            <span
                v-if="showTrailingLocation"
                class="text-muted-foreground min-w-0 truncate text-xs leading-tight"
            >
                {{ locationName }}
            </span>
        </div>

        <span
            v-if="checkinSeconds !== null"
            class="text-muted-foreground justify-self-end font-mono text-xs tabular-nums"
            data-testid="lzp-overlay-header-elapsed"
        >
            {{ formatCompactDuration(checkinSeconds) }}
        </span>
        <span v-else class="text-muted-foreground justify-self-end text-xs">never</span>

    </div>
</template>
